import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getUserAvatarUrl, handleUserAvatarError } from '../utils/avatars';
import Pagination from './Pagination';
import CountryDisplay from './CountryDisplay';
import CountrySelect from './CountrySelect';
import SocialIcons from './SocialIcons';
import './UsersList.css';
import './UsersListMobileFilters.css';
import { localeFor } from '../utils/timeFormat';

const UsersList = () => {
  const { user: currentUser } = useAuth();
  const { t, language } = useLanguage();
  const location = useLocation();
  // Возврат из карточки гонщика: восстанавливаем страницу, поиск и фильтры,
  // с которыми список был открыт (кладем их в state ссылки на /riders/:id)
  const restored = location.state?.usersList || null;
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: restored?.page || 1,
    per_page: 10,
    total_pages: 1,
    total_count: 0
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerTarget = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(restored?.search || '');
  const [searchInput, setSearchInput] = useState(restored?.search || '');
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [sendingReauthEmailId, setSendingReauthEmailId] = useState(null);
  const [avatarTooltip, setAvatarTooltip] = useState(null);
  const [avatarTooltipPosition, setAvatarTooltipPosition] = useState({ top: 0, left: 0 });
  const [powerGroupTooltipPosition, setPowerGroupTooltipPosition] = useState({ top: 0, left: 0 });
  const [showPowerGroupInfo, setShowPowerGroupInfo] = useState(false);
  const powerGroupInfoRef = useRef(null);
  const initialPageRef = useRef(restored?.page || 1);
  const buttonRefs = useRef({});
  const nameRefs = useRef({});
  
  // Фильтры
  const [filters, setFilters] = useState(restored?.filters || {
    country: '',
    team_id: '',
    power_group_letter: '',
    age_group: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    countries: [],
    country_counts: {},
    teams: [],
    power_groups: []
  });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const headerRefs = useRef({});
  
  // Mobile filters state
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileFilters, setMobileFilters] = useState({
    country: '',
    team_id: '',
    power_group_letter: '',
    age_group: ''
  });

  const requestUsers = (page, search, filtersToUse) => {
    const params = new URLSearchParams();
    params.append('page', page);
    if (search) params.append('search', search);
    if (filtersToUse.country) params.append('country', filtersToUse.country);
    if (filtersToUse.team_id) params.append('team_id', filtersToUse.team_id);
    if (filtersToUse.power_group_letter) params.append('power_group_letter', filtersToUse.power_group_letter);
    if (filtersToUse.age_group) params.append('age_group', filtersToUse.age_group);

    return axios.get(`/api/users?${params.toString()}`);
  };

  const fetchUsers = async (page = 1, search = '', currentFilters = null, append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    // Используем переданные фильтры или текущие из состояния
    const filtersToUse = currentFilters || filters;
    
    try {
      const response = await requestUsers(page, search, filtersToUse);
      if (append) {
        setUsers(prev => [...prev, ...response.data.users]);
        setDisplayedUsers(prev => [...prev, ...response.data.users]);
      } else {
        setUsers(response.data.users);
        setDisplayedUsers(response.data.users);
      }
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      if (append) {
        setIsLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Возврат из карточки на мобильном: там список бесконечный, поэтому
  // добираем все страницы с первой по сохраненную, иначе начало списка пропадет
  const fetchUsersUpTo = async (lastPage, search, currentFilters) => {
    setLoading(true);
    setError(null);

    try {
      const pages = Array.from({ length: lastPage }, (_, i) => i + 1);
      const responses = await Promise.all(pages.map(page => requestUsers(page, search, currentFilters)));
      const merged = responses.flatMap(response => response.data.users);

      setUsers(merged);
      setDisplayedUsers(merged);
      setPagination(responses[responses.length - 1].data.pagination);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get('/api/users/filter_options');
      console.log('Filter options received:', response.data);
      setFilterOptions(response.data);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.filter-header') && !event.target.closest('.filter-dropdown-fixed')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && observerTarget.current) {
      const observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && !isLoadingMore && pagination.current_page < pagination.total_pages) {
            fetchUsers(pagination.current_page + 1, searchQuery, filters, true);
          }
        },
        { threshold: 0.1 }
      );

      observer.observe(observerTarget.current);
      return () => observer.disconnect();
    }
  }, [isMobile, isLoadingMore, pagination, searchQuery, filters]);

  useEffect(() => {
    // Первая загрузка после возврата из карточки идет на сохраненную страницу,
    // дальше смена поиска или фильтров, как и раньше, сбрасывает список на первую
    const page = initialPageRef.current;
    initialPageRef.current = 1;

    if (page > 1 && isMobile) {
      fetchUsersUpTo(page, searchQuery, filters);
    } else {
      fetchUsers(page, searchQuery, filters, false);
    }
  }, [searchQuery, filters.country, filters.team_id, filters.power_group_letter, filters.age_group]);

  // Точка возврата для карточки гонщика: текущая страница списка с поиском и фильтрами
  const riderLinkState = () => ({
    backTo: {
      pathname: '/users',
      labelKey: 'users.back_to_list',
      state: {
        usersList: {
          page: pagination.current_page,
          search: searchQuery,
          filters
        }
      }
    }
  });

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [filterName]: value
      };
      return newFilters;
    });
    setOpenDropdown(null);
  };

  const toggleDropdown = (dropdownName) => {
    if (openDropdown === dropdownName) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(dropdownName);
      const headerElement = headerRefs.current[dropdownName];
      if (headerElement) {
        const rect = headerElement.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX
        });
      }
    }
  };

  const clearFilters = () => {
    setFilters({
      country: '',
      team_id: '',
      power_group_letter: '',
      age_group: ''
    });
  };

  const handleMobileFilterChange = (filterName, value) => {
    const newFilters = {
      ...filters,
      [filterName]: value
    };
    setFilters(newFilters);
    setMobileFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearMobileFilters = () => {
    setFilters({
      country: '',
      team_id: '',
      power_group_letter: '',
      age_group: ''
    });
    setMobileFilters({
      country: '',
      team_id: '',
      power_group_letter: '',
      age_group: ''
    });
  };

  const hasActiveFilters = filters.country || filters.team_id || filters.power_group_letter || filters.age_group;

  const handleBlockUser = async (userId) => {
    if (!window.confirm(t('users.confirmBlock'))) {
      return;
    }

    try {
      await axios.post(`/api/users/${userId}/block`);
      fetchUsers(pagination.current_page, searchQuery, filters);
    } catch (err) {
      alert(t('users.blockError') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUnblockUser = async (userId) => {
    if (!window.confirm(t('users.confirmUnblock'))) {
      return;
    }

    try {
      await axios.post(`/api/users/${userId}/unblock`);
      fetchUsers(pagination.current_page, searchQuery, filters);
    } catch (err) {
      alert(t('users.unblockError') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  // Ручная отправка письма с просьбой переподключить ROUVY (язык письма — из профиля пользователя)
  const handleSendRouvyReauthEmail = async (user) => {
    if (!window.confirm(t('users.confirmRouvyReauthEmail'))) {
      return;
    }

    setSendingReauthEmailId(user.id);

    try {
      const response = await axios.post(`/api/users/${user.id}/send_rouvy_reauth_email`);
      alert(response.data.message);
    } catch (err) {
      alert(t('users.rouvyReauthEmailError') + ': ' + (err.response?.data?.error || err.message));
    } finally {
      setSendingReauthEmailId(null);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchUsers(newPage, searchQuery, filters);
    }
  };

  const handleInviteToTeam = async (userId) => {
    // Определяем команду: первая управляемая менеджером команда или команда из капитанского членства
    const teamId = currentUser?.administered_teams?.[0]?.id
      || currentUser?.team_memberships_summary?.find(m => m.role === 'captain' && !m.event_team)?.team_id
      || currentUser?.team?.id;

    try {
      const response = await axios.post('/api/team_invitations', { user_id: userId, team_id: teamId });
      alert(response.data.message);
      fetchUsers(pagination.current_page, searchQuery, filters);
    } catch (err) {
      alert(t('users.inviteError') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRemoveFromTeam = async (userId) => {
    if (!window.confirm(t('users.confirmRemoveFromTeam'))) {
      return;
    }

    try {
      const response = await axios.post(`/api/users/${userId}/remove_from_team`);
      alert(response.data.message);
      fetchUsers(pagination.current_page, searchQuery, filters);
    } catch (err) {
      alert(t('users.removeFromTeamError') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  const canInviteUser = (user) => {
    if (!currentUser || user.blocked_at || user.id === currentUser.id) return false;
    // Менеджер хотя бы одной команды или капитан
    const isManager = currentUser.administered_teams?.length > 0;
    // В команды под событие приглашают со страницы события
    const isCaptain = currentUser.team_memberships_summary?.some(m => m.role === 'captain' && !m.event_team);
    return isManager || isCaptain;
  };

  const canRemoveFromTeam = (user) => {
    return currentUser?.team_id && 
           currentUser?.team?.admin_id === currentUser?.id && 
           user.team_id === currentUser?.team_id && 
           user.id !== currentUser?.id;
  };

  const handleMouseEnter = (e, tooltipId) => {
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    
    const tempTooltip = document.createElement('div');
    tempTooltip.className = 'tooltip-global';
    tempTooltip.style.visibility = 'hidden';
    tempTooltip.style.position = 'fixed';
    tempTooltip.style.top = '-9999px';
    
    let tooltipText = '';
    if (tooltipId.startsWith('block-')) tooltipText = t('users.block');
    if (tooltipId.startsWith('unblock-')) tooltipText = t('users.unblock');
    if (tooltipId.startsWith('invite-')) tooltipText = t('users.inviteToTeam');
    if (tooltipId.startsWith('remove-')) tooltipText = t('users.removeFromTeam');
    
    tempTooltip.textContent = tooltipText;
    document.body.appendChild(tempTooltip);
    const tooltipWidth = tempTooltip.offsetWidth;
    document.body.removeChild(tempTooltip);
    
    setTooltipPosition({
      top: rect.top - 40,
      left: rect.left + rect.width / 2 - tooltipWidth / 2
    });
    setActiveTooltip(tooltipId);
  };

  const handleAvatarMouseEnter = (e, lastActivity) => {
    const avatar = e.currentTarget;
    const rect = avatar.getBoundingClientRect();
    
    setAvatarTooltipPosition({
      top: rect.top - 40,
      left: rect.left + rect.width / 2
    });
    setAvatarTooltip(lastActivity);
  };

  const handleAvatarMouseLeave = () => {
    setAvatarTooltip(null);
  };

  const handlePowerGroupInfoClick = (e) => {
    e.stopPropagation();
    const icon = e.currentTarget;
    const rect = icon.getBoundingClientRect();
    
    setPowerGroupTooltipPosition({
      top: rect.top - 80,
      left: rect.left + rect.width / 2
    });
    setShowPowerGroupInfo(!showPowerGroupInfo);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPowerGroupInfo && powerGroupInfoRef.current && !powerGroupInfoRef.current.contains(event.target)) {
        setShowPowerGroupInfo(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPowerGroupInfo]);

  const handleMouseLeave = () => {
    setActiveTooltip(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString(localeFor(language));
  };

  const calculateAgeGroup = (birthDate) => {
    if (!birthDate) return '-';
    
    const today = new Date();
    const birth = new Date(birthDate);
    const age = today.getFullYear() - birth.getFullYear();
    
    if (age < 19) return '<19';
    if (age >= 19 && age <= 34) return '19-34';
    if (age >= 35 && age <= 39) return '35-39';
    if (age >= 40 && age <= 44) return '40-44';
    if (age >= 45 && age <= 49) return '45-49';
    if (age >= 50 && age <= 54) return '50-54';
    if (age >= 55 && age <= 59) return '55-59';
    if (age >= 60 && age <= 64) return '60-64';
    if (age >= 65 && age <= 69) return '65-69';
    if (age >= 70 && age <= 74) return '70-74';
    if (age >= 75 && age <= 79) return '75-79';
    return '80+';
  };

  const ageGroups = ['<19', '19-34', '35-39', '40-44', '45-49', '50-54', '55-59', '60-64', '65-69', '70-74', '75-79', '80+'];

  // Check if text overflows and needs marquee animation
  useEffect(() => {
    // Check all name elements for overflow after render
    Object.keys(nameRefs.current).forEach(key => {
      const element = nameRefs.current[key];
      if (element) {
        // For user card names
        const inner = element.querySelector('.user-card-name-inner');
        if (inner) {
          // Check if text overflows
          const isOverflowing = false; // element.scrollWidth > element.clientWidth;
          if (!isOverflowing) {
            inner.classList.add('no-animation');
          } else {
            inner.classList.remove('no-animation');
          }
        }
        
        // For power group badges
        const badgeInner = element.querySelector('.power-group-badge-inner');
        if (badgeInner) {
          // Check if text overflows
          const isOverflowing = false; // element.scrollWidth > element.clientWidth;
          if (!isOverflowing) {
            badgeInner.classList.add('no-animation');
          } else {
            badgeInner.classList.remove('no-animation');
          }
        }
      }
    });
  }, [users, displayedUsers, language]);

  if (loading && users.length === 0) {
    return <div className="users-list-container"><div className="loading">{t('common.loading')}</div></div>;
  }

  if (error) {
    return <div className="users-list-container"><div className="error">{error}</div></div>;
  }

  return (
    <div className={`users-list-container ${isMobile ? 'mobile-view' : ''}`}>
      <div className="users-list-header">
        <h2>{t('users.title')}</h2>
        
        <div className="users-search">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('users.searchPlaceholder')}
            className="search-input"
          />
          <button type="submit" className="search-button">
            {t('users.search')}
          </button>
          {searchQuery && (
            <button type="button" onClick={handleClearSearch} className="clear-search-button">
              {t('users.clearSearch')}
            </button>
          )}
        </form>
        {searchQuery && (
          <div className="search-info">
            {t('users.searchResults')}: "{searchQuery}"
          </div>
        )}
      </div>
      
        <div className="users-stats-bar">
          {hasActiveFilters && (
            <button onClick={clearFilters} className="clear-filters-btn">
              {t('users.clearFilters')}
            </button>
          )}
          {/* Mobile Filters */}
          {isMobile && (
            <div className="mobile-filters-wrapper">
              <button 
                className="mobile-filters-toggle"
                onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
              >
                <span>{t('users.filters')}</span>
                <span className={`mobile-filters-arrow ${mobileFiltersOpen ? 'mobile-filters-arrow-open' : ''}`}>
                  ▼
                </span>
              </button>
              
              <div className={`mobile-filters-panel ${mobileFiltersOpen ? 'mobile-filters-panel-open' : ''}`}>
                <div className="mobile-filters-grid">
                  <div className="mobile-filters-item">
                    <label htmlFor="mobile-filter-country">{t('users.country')}:</label>
                    <CountrySelect
                      value={filters.country}
                      onChange={(value) => handleMobileFilterChange('country', value)}
                      language={language}
                      placeholder={t('users.allCountries')}
                      countryCounts={filterOptions.country_counts}
                      onlyCountries={filterOptions.countries}
                    />
                  </div>
                  
                  <div className="mobile-filters-item">
                    <label htmlFor="mobile-filter-team">{t('users.team')}:</label>
                    <select
                      id="mobile-filter-team"
                      value={filters.team_id}
                      onChange={(e) => handleMobileFilterChange('team_id', e.target.value)}
                      className="mobile-filters-select"
                    >
                      <option value="">{t('users.allTeams')}</option>
                      {filterOptions.teams.map(team => (
                        <option key={team.id} value={String(team.id)}>{team.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mobile-filters-item">
                    <label htmlFor="mobile-filter-power" dangerouslySetInnerHTML={{ __html: t('users.powerGroup') }}></label>
                    <select
                      id="mobile-filter-power"
                      value={filters.power_group_letter}
                      onChange={(e) => handleMobileFilterChange('power_group_letter', e.target.value)}
                      className="mobile-filters-select"
                    >
                      <option value="">{t('users.allGroups')}</option>
                      {filterOptions.power_groups.map(group => (
                        <option key={group} value={group}>{t('users.group')} {group}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mobile-filters-item">
                    <label htmlFor="mobile-filter-age" dangerouslySetInnerHTML={{ __html: t('users.ageGroup') }}></label>
                    <select
                      id="mobile-filter-age"
                      value={filters.age_group}
                      onChange={(e) => handleMobileFilterChange('age_group', e.target.value)}
                      className="mobile-filters-select"
                    >
                      <option value="">{t('users.allAgeGroups')}</option>
                      {ageGroups.map(group => (
                        <option key={group} value={group}>{group}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {hasActiveFilters && (
                  <button 
                    className="mobile-filters-clear"
                    onClick={clearMobileFilters}
                  >
                    {t('users.clearFilters')}
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="users-stats">
            {t('users.totalUsers')}: {pagination.total_count}
          </div>
        </div>
      </div>

      <div className="users-list-body">
        {isMobile ? (
          <div className="users-cards-container">
            <div className="users-cards-grid">
              {displayedUsers.map((user) => (
                <div key={user.id} className={`user-card ${user.blocked_at ? 'blocked-user' : ''}`}>
                  <div className="user-card-header">
                    <div className='user-header-line'>
                      <div className="user-avatar-wrapper">
                        <img 
                          src={getUserAvatarUrl(user.avatar)} 
                          alt={user.nickname} 
                          className={`user-card-avatar ${user.admin ? 'admin-avatar' : ''}`}
                          onError={handleUserAvatarError}
                        />
                        {user.gender && (
                          <span className={`gender-badge ${user.gender === 'male' ? 'gender-badge-male' : 'gender-badge-female'}`}>
                            {user.gender === 'male' ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <circle cx="10" cy="14" r="5"/>
                                <path d="M19 5l-7 7M19 5h-5M19 5v5"/>
                              </svg>
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <circle cx="12" cy="8" r="5"/>
                                <path d="M12 13v8M9 18h6"/>
                              </svg>
                            )}
                          </span>
                        )}
                      </div>
                      <div className="user-card-details">
                        <div className="user-card-row">
                          <CountryDisplay country={user.country} language={language} />
                        </div>
                        
                        <div className="user-card-row">
                          {user.team ? (
                            <a href={`/teams?search=${encodeURIComponent(user.team.name)}`} className="team-link">
                              {user.team.name}
                            </a>
                          ) : (
                            <span>-</span>
                          )}
                        </div>
                        
                        <div className="user-card-row">
                          <span className="user-card-groups">
                            <span 
                              className="power-group-badge"
                              ref={el => nameRefs.current[`power-${user.id}`] = el}
                            >
                              <span className="power-group-badge-inner">
                                {user.power_group_letter ? user.power_group_letter : '-'}
                                {user.power_group && (
                                  <span className="power-group-value-mobile">
                                    ({user.power_group} {t('users.powerUnit')})
                                  </span>
                                )}
                              </span>
                            </span>
                            <span className="age-group-badge">{calculateAgeGroup(user.birth_date)}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="user-card-info">
                      <div 
                        className="user-card-name"
                        ref={el => nameRefs.current[`card-${user.id}`] = el}
                      >
                        <span className="user-card-name-inner">
                          {language !== 'ru' && user.first_name_eng && user.last_name_eng
                            ? `${user.first_name_eng} ${user.last_name_eng}`
                            : `${user.first_name} ${user.last_name}`}
                        </span>
                      </div>
                      <div className="user-card-nickname">
                        <Link className="user-profile-link" to={`/riders/${user.id}`} state={riderLinkState()}>
                          @{user.nickname}
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className="user-card-actions">
                    {currentUser?.admin && !user.admin && (
                      user.blocked_at ? (
                        <button
                          onClick={() => handleUnblockUser(user.id)}
                          className="btn-icon btn-icon-unblock"
                          aria-label={t('users.unblock')}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                            <path d="M9 12l2 2 4-4"/>
                          </svg>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleBlockUser(user.id)}
                          className="btn-icon btn-icon-block"
                          aria-label={t('users.block')}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                          </svg>
                        </button>
                      )
                    )}
                    {currentUser?.admin && user.rouvy_needs_reauthorization && (
                      <button
                        onClick={() => handleSendRouvyReauthEmail(user)}
                        disabled={sendingReauthEmailId === user.id}
                        className="btn-icon btn-icon-reauth-email"
                        aria-label={t('users.sendRouvyReauthEmail')}
                        title={t('users.sendRouvyReauthEmail')}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                          <polyline points="22,6 12,13 2,6"/>
                        </svg>
                      </button>
                    )}
                    {canInviteUser(user) && (
                      <button
                        onClick={() => handleInviteToTeam(user.id)}
                        className="btn-icon btn-icon-invite"
                        aria-label={t('users.inviteToTeam')}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <line x1="19" y1="8" x2="19" y2="14"/>
                          <line x1="22" y1="11" x2="16" y2="11"/>
                        </svg>
                      </button>
                    )}
                    {canRemoveFromTeam(user) && (
                      <button
                        onClick={() => handleRemoveFromTeam(user.id)}
                        className="btn-icon btn-icon-remove"
                        aria-label={t('users.removeFromTeam')}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <line x1="22" y1="11" x2="16" y2="11"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {isLoadingMore && (
              <div className="loading-more">{t('common.loading')}...</div>
            )}
            <div ref={observerTarget} className="observer-target"></div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="users-table">
          <thead>
            <tr>
              <th>{t('users.fullName')}</th>
              <th>
                <div 
                  className="filter-header" 
                  onClick={() => toggleDropdown('country')}
                  ref={el => headerRefs.current['country'] = el}
                >
                  <span>{t('users.country')}</span>
                  <svg className={`filter-icon ${filters.country ? 'active' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </th>
              <th>
                <div 
                  className="filter-header" 
                  onClick={() => toggleDropdown('team')}
                  ref={el => headerRefs.current['team'] = el}
                >
                  <span>{t('users.team')}</span>
                  <svg className={`filter-icon ${filters.team_id ? 'active' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </th>
              <th>
                <div 
                  className="filter-header" 
                  onClick={() => toggleDropdown('power_group')}
                  ref={el => headerRefs.current['power_group'] = el}
                >
                  <svg 
                    className="info-icon" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    onClick={handlePowerGroupInfoClick}
                    style={{ cursor: 'pointer', marginLeft: '4px', width: '16px', height: '16px' }}
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  <span dangerouslySetInnerHTML={{ __html: t('users.powerGroup') }}></span>
                  <svg className={`filter-icon ${filters.power_group_letter ? 'active' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </th>
              <th>
                <div 
                  className="filter-header" 
                  onClick={() => toggleDropdown('age_group')}
                  ref={el => headerRefs.current['age_group'] = el}
                >
                  <span dangerouslySetInnerHTML={{ __html: t('users.ageGroup') }}></span>
                  <svg className={`filter-icon ${filters.age_group ? 'active' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </th>
              {currentUser?.admin && <th>{t('users.status')}</th>}
              <th>{t('users.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className={user.blocked_at ? 'blocked-user' : ''}>
                <td>
                  <div className="user-name">
                    <img 
                      src={getUserAvatarUrl(user.avatar)} 
                      alt={user.nickname} 
                      className={`user-avatar-small ${user.admin ? 'admin-avatar' : ''}`}
                      onError={handleUserAvatarError}
                      onMouseEnter={(e) => handleAvatarMouseEnter(e, user.last_activity)}
                      onMouseLeave={handleAvatarMouseLeave}
                      style={{ cursor: 'pointer' }}
                    />
                    {user.gender && (
                      <span className={`gender-icon ${user.gender === 'male' ? 'gender-male' : 'gender-female'}`}>
                        {user.gender === 'male' ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="10" cy="14" r="5"/>
                            <path d="M19 5l-7 7M19 5h-5M19 5v5"/>
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="8" r="5"/>
                            <path d="M12 13v8M9 18h6"/>
                          </svg>
                        )}
                      </span>
                    )}
                    <div>
                      <div className="user-name-text">
                        <Link className="user-profile-link" to={`/riders/${user.id}`} state={riderLinkState()} title={t('results.open_rider')}>
                          {language !== 'ru' && user.first_name_eng && user.last_name_eng
                            ? `${user.first_name_eng} ${user.last_name_eng}`
                            : `${user.first_name} ${user.last_name}`}
                        </Link>
                      </div>
                      <div className="member-nickname">@{user.nickname}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <CountryDisplay country={user.country} language={language} />
                </td>
                <td>
                  {user.team ? (
                    <a href={`/teams?search=${encodeURIComponent(user.team.name)}`} className="team-link">
                      {user.team.name}
                    </a>
                  ) : (
                    '-'
                  )}
                </td>
                <td 
                  className="power-group-cell"
                >
                  {user.power_group_letter ? user.power_group_letter : '-'}
                  {user.power_group && (
                    <span className="power-group-value">
                      ({user.power_group} {user.power_group && t('users.powerUnit')})
                    </span>
                  )}
                </td>
                <td className="age-group-cell">
                  {calculateAgeGroup(user.birth_date)}
                </td>
                {currentUser?.admin && (
                  <td>
                    {user.blocked_at ? (
                      <span className="user-status-blocked">{t('users.blocked')}</span>
                    ) : (
                      <span className="user-status-active">{t('users.active')}</span>
                    )}
                  </td>
                )}
                <td>
                  <div className="user-actions">
                    {currentUser?.admin && !user.admin && (
                      user.blocked_at ? (
                        <button
                          onClick={() => handleUnblockUser(user.id)}
                          onMouseEnter={(e) => handleMouseEnter(e, `unblock-${user.id}`)}
                          onMouseLeave={handleMouseLeave}
                          className="btn-icon btn-icon-unblock"
                          aria-label={t('users.unblock')}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                            <path d="M9 12l2 2 4-4"/>
                          </svg>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleBlockUser(user.id)}
                          onMouseEnter={(e) => handleMouseEnter(e, `block-${user.id}`)}
                          onMouseLeave={handleMouseLeave}
                          className="btn-icon btn-icon-block"
                          aria-label={t('users.block')}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                          </svg>
                        </button>
                      )
                    )}
                    {currentUser?.admin && user.rouvy_needs_reauthorization && (
                      <button
                        onClick={() => handleSendRouvyReauthEmail(user)}
                        disabled={sendingReauthEmailId === user.id}
                        className="btn-icon btn-icon-reauth-email"
                        aria-label={t('users.sendRouvyReauthEmail')}
                        title={t('users.sendRouvyReauthEmail')}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                          <polyline points="22,6 12,13 2,6"/>
                        </svg>
                      </button>
                    )}
                    {canInviteUser(user) && (
                      <button
                        onClick={() => handleInviteToTeam(user.id)}
                        onMouseEnter={(e) => handleMouseEnter(e, `invite-${user.id}`)}
                        onMouseLeave={handleMouseLeave}
                        className="btn-icon btn-icon-invite"
                        aria-label={t('users.inviteToTeam')}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <line x1="19" y1="8" x2="19" y2="14"/>
                          <line x1="22" y1="11" x2="16" y2="11"/>
                        </svg>
                      </button>
                    )}
                    {canRemoveFromTeam(user) && (
                      <button
                        onClick={() => handleRemoveFromTeam(user.id)}
                        onMouseEnter={(e) => handleMouseEnter(e, `remove-${user.id}`)}
                        onMouseLeave={handleMouseLeave}
                        className="btn-icon btn-icon-remove"
                        aria-label={t('users.removeFromTeam')}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <line x1="22" y1="11" x2="16" y2="11"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
            </table>
          </div>
        )}
      </div>

      {!isMobile && (
      <div className="users-list-footer">
        <Pagination pagination={pagination} onPageChange={handlePageChange} t={t} compact />
      </div>
      )}

      {openDropdown === 'country' && (
        <div 
          className="filter-dropdown-fixed"
          style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
        >
          <div 
            className={`filter-dropdown-option ${!filters.country ? 'selected' : ''}`}
            onClick={() => handleFilterChange('country', '')}
          >
            {t('users.allCountries')}
          </div>
          {filterOptions.countries.map(country => (
            <div
              key={country}
              className={`filter-dropdown-option ${filters.country === country ? 'selected' : ''}`}
              onClick={() => handleFilterChange('country', country)}
            >
              <CountryDisplay country={country} language={language} />
              {filterOptions.country_counts[country] && (
                <span className="country-filter-count">({filterOptions.country_counts[country]})</span>
              )}
            </div>
          ))}
        </div>
      )}

      {openDropdown === 'team' && (
        <div 
          className="filter-dropdown-fixed"
          style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
        >
          <div 
            className={`filter-dropdown-option ${!filters.team_id ? 'selected' : ''}`}
            onClick={() => handleFilterChange('team_id', '')}
          >
            {t('users.allTeams')}
          </div>
          {filterOptions.teams.map(team => (
            <div 
              key={team.id} 
              className={`filter-dropdown-option ${filters.team_id === String(team.id) ? 'selected' : ''}`}
              onClick={() => handleFilterChange('team_id', String(team.id))}
            >
              {team.name}
            </div>
          ))}
        </div>
      )}

      {openDropdown === 'power_group' && (
        <div 
          className="filter-dropdown-fixed"
          style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
        >
          <div 
            className={`filter-dropdown-option ${!filters.power_group_letter ? 'selected' : ''}`}
            onClick={() => handleFilterChange('power_group_letter', '')}
          >
            {t('users.allGroups')}
          </div>
          {filterOptions.power_groups.map(group => (
            <div 
              key={group} 
              className={`filter-dropdown-option ${filters.power_group_letter === group ? 'selected' : ''}`}
              onClick={() => handleFilterChange('power_group_letter', group)}
            >
              {t('users.group')} {group}
            </div>
          ))}
        </div>
      )}

      {openDropdown === 'age_group' && (
        <div 
          className="filter-dropdown-fixed"
          style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
        >
          <div 
            className={`filter-dropdown-option ${!filters.age_group ? 'selected' : ''}`}
            onClick={() => handleFilterChange('age_group', '')}
          >
            {t('users.allAgeGroups')}
          </div>
          {ageGroups.map(group => (
            <div 
              key={group} 
              className={`filter-dropdown-option ${filters.age_group === group ? 'selected' : ''}`}
              onClick={() => handleFilterChange('age_group', group)}
            >
              {group}
            </div>
          ))}
        </div>
      )}

      {activeTooltip && (
        <div 
          className="tooltip tooltip-global"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            opacity: 1
          }}
        >
          {activeTooltip.startsWith('block-') && t('users.block')}
          {activeTooltip.startsWith('unblock-') && t('users.unblock')}
          {activeTooltip.startsWith('invite-') && t('users.inviteToTeam')}
          {activeTooltip.startsWith('remove-') && t('users.removeFromTeam')}
        </div>
      )}

      {avatarTooltip && (
        <div 
          className="tooltip tooltip-global"
          style={{
            top: `${avatarTooltipPosition.top}px`,
            left: `${avatarTooltipPosition.left}px`,
            transform: 'translateX(-50%)',
            opacity: 1
          }}
        >
          {t('users.lastActivity')}: {formatDate(avatarTooltip)}
        </div>
      )}

      {showPowerGroupInfo && (
        <div 
          ref={powerGroupInfoRef}
          className="tooltip tooltip-global"
          style={{
            top: `${powerGroupTooltipPosition.top}px`,
            left: `${powerGroupTooltipPosition.left}px`,
            transform: 'translateX(-50%)',
            opacity: 1,
            maxWidth: '300px',
            whiteSpace: 'normal',
            pointerEvents: 'auto'
          }}
        >
          {t('users.powerGroupTooltip')}
        </div>
      )}
    </div>
  );
};

export default UsersList;